/* import __COLOCATED_TEMPLATE__ from './bulk-schedule-publish-modal.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type IntlService from 'ember-intl/services/intl';
import moment from 'moment-timezone';
import type CustomReportsService from 'embercom/services/custom-reports-service';

interface Args {
  onClose: () => void;
  onSubmit: (contentUpdateType: string, scheduledAt: string, timezone: string) => void;
}

export default class BulkSchedulePublishModal extends Component<{ Args: Args }> {
  @service declare appService: AppService;
  @service declare customReportsService: CustomReportsService;
  @service declare intl: IntlService;

  @tracked selectedAction: 'publish' | 'unpublish' = 'publish';
  @tracked _selectedTimezone: string | null = null;
  @tracked _scheduledDate: Date | null = null;

  get selectedTimezone(): string {
    return this._selectedTimezone ?? this.appService.app.timezone;
  }

  set selectedTimezone(value: string) {
    this._selectedTimezone = value;
  }

  get scheduledDate(): Date {
    return this._scheduledDate ?? this.defaultScheduledDate;
  }

  set scheduledDate(value: Date) {
    this._scheduledDate = value;
  }

  get defaultScheduledDate() {
    return moment
      .tz(this.appService.app.timezone)
      .add(1, 'day')
      .hour(9)
      .minute(0)
      .second(0)
      .millisecond(0)
      .toDate();
  }

  get today() {
    return new Date();
  }

  get modalTitle(): string {
    return this.intl.t('knowledge-hub.filterable-list.bulk-actions.schedule-publish.modal-title');
  }

  get timezoneOptions(): { text: string; value: string }[] {
    return this.customReportsService.timezones().map((tz) => ({
      text: tz.description,
      value: tz.description,
    }));
  }

  get selectedTimezoneDescription(): string {
    let tz = this.customReportsService.timezones().find((t) => t.value === this.selectedTimezone);
    return tz?.description ?? this.selectedTimezone;
  }

  get scheduledDateError(): string | null {
    if (this.scheduledDate <= new Date()) {
      return this.intl.t('knowledge-hub.filterable-list.bulk-actions.schedule-publish.date-error');
    }
    return null;
  }

  get canSave() {
    return !this.scheduledDateError;
  }

  private toSelectedTimezoneISO(date: Date): string {
    let appTz = this.appService.app.timezone;
    if (this.selectedTimezone === appTz) {
      return date.toISOString();
    }
    let wallClock = moment(date).tz(appTz).format('YYYY-MM-DD HH:mm:ss');
    return moment.tz(wallClock, 'YYYY-MM-DD HH:mm:ss', this.selectedTimezone).toISOString();
  }

  @action onActionChange(value: 'publish' | 'unpublish') {
    this.selectedAction = value;
  }

  @action onScheduledDateChange(date: Date) {
    this.scheduledDate = date;
  }

  @action onTimezoneChange(description: string) {
    let tz = this.customReportsService.timezones().find((t) => t.description === description);
    if (tz) {
      this.selectedTimezone = tz.value;
    }
  }

  @action submit() {
    if (!this.canSave) {
      return;
    }
    this.args.onSubmit(
      this.selectedAction,
      this.toSelectedTimezoneISO(this.scheduledDate),
      this.selectedTimezone,
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::FilterableList::BulkActions::BulkSchedulePublishModal': typeof BulkSchedulePublishModal;
    'knowledge-hub/filterable-list/bulk-actions/bulk-schedule-publish-modal': typeof BulkSchedulePublishModal;
  }
}
